/**
 * Platform Admin > Features > "Delivery grain per customer".
 *
 * The install-wide "PO Line Schedules" toggle above this panel is the
 * default; each customer company can override it here so a schedule-line
 * ERP feed and a plain PO-line feed can share one install and each be
 * judged (dashboards, PO Lines grid, drill-downs, forecast) by its own grain.
 *
 * Rows save immediately (one PATCH per change) — this is Helix-only config
 * and independent of the platform-settings Save button.
 */
import { useCallback, useEffect, useState } from 'react';
import { platformSettingsAPI, type CustomerDeliveryGrain, type DeliveryGrainSetting } from '../../../lib/api';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Layers, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface DeliveryGrainPanelProps {
  /** Current (possibly unsaved) value of the platform toggle, for the "inherit" hint. */
  platformPoLineSchedules: boolean;
}

const GRAIN_LABELS: Record<DeliveryGrainSetting, string> = {
  platform: 'Inherit platform',
  schedules: 'Schedule lines',
  po_lines: 'PO lines',
};

export function DeliveryGrainPanel({ platformPoLineSchedules }: DeliveryGrainPanelProps) {
  const [customers, setCustomers] = useState<CustomerDeliveryGrain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformSettingsAPI.getDeliveryGrains();
      setCustomers(res.data.customers ?? []);
    } catch (err: unknown) {
      console.error('Failed to load delivery grain settings:', err);
      setError('Could not load the per-customer delivery grain. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onChange = async (companyId: number, grain: DeliveryGrainSetting) => {
    setSavingId(companyId);
    try {
      const res = await platformSettingsAPI.updateDeliveryGrain(companyId, grain);
      setCustomers(prev => prev.map(c => (
        c.id === companyId
          ? { ...c, delivery_grain: res.data.delivery_grain, effective_grain: res.data.effective_grain }
          : c
      )));
      toast.success(`Delivery grain for ${res.data.name}: ${GRAIN_LABELS[res.data.delivery_grain]}`);
    } catch (err: unknown) {
      console.error('Failed to update delivery grain:', err);
      toast.error('Failed to update delivery grain');
    } finally {
      setSavingId(null);
    }
  };

  const platformLabel = platformPoLineSchedules ? 'Schedule lines' : 'PO lines';

  return (
    <div className="p-4 border rounded-lg" data-testid="delivery-grain-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <Layers className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <Label className="text-base font-medium">Delivery grain per customer</Label>
            <p className="text-sm text-muted-foreground">
              Override the PO Line Schedules default for one customer. Schedule-line customers are
              measured on their schedule rows (Scheduled dashboard, schedule columns); PO-line customers on
              their PO-line headers (Current Performance dashboard). Group roll-ups sum each customer by its own grain.
              Inherit platform currently means <span className="font-medium">{platformLabel}</span>.
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} aria-label="Reload delivery grain">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {loading && customers.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading customers…
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive py-2" role="alert">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {!loading && !error && customers.length === 0 && (
        <p className="text-sm text-muted-foreground py-2">No customer companies on this install yet.</p>
      )}

      {customers.length > 0 && (
        <div className="space-y-2">
          {customers.map((c) => (
            <div
              key={c.id}
              data-testid={`delivery-grain-row-${c.id}`}
              className="flex flex-wrap items-center justify-between gap-2 border rounded-md px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium truncate">{c.name}</span>
                {!c.is_active && <Badge variant="outline">Inactive</Badge>}
                <Badge variant={c.delivery_grain === 'platform' ? 'secondary' : 'outline'}>
                  Effective: {GRAIN_LABELS[c.effective_grain]}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                {savingId === c.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <Select
                  value={c.delivery_grain}
                  onValueChange={(v) => onChange(c.id, v as DeliveryGrainSetting)}
                  disabled={savingId === c.id}
                >
                  <SelectTrigger className="w-[200px]" aria-label={`Delivery grain for ${c.name}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="platform">{GRAIN_LABELS.platform}</SelectItem>
                    <SelectItem value="schedules">{GRAIN_LABELS.schedules}</SelectItem>
                    <SelectItem value="po_lines">{GRAIN_LABELS.po_lines}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DeliveryGrainPanel;
